import { useState } from 'react';
import {
  apiErrorText,
  useActivateFsnbEditionMutation,
  useDeleteFsnbEditionMutation,
  useRetryFsnbEditionMutation,
  type FsnbEdition,
} from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import {
  editionStatusLabel,
  editionStatusTone,
  fmtDateTime,
  sizeLabel,
  sourceLabel,
  total,
} from '../../normBaseLabels';
import {
  Alert,
  BlockTitle,
  Divider,
  Grid,
  Grow,
  Lead,
  Muted,
  Note,
  Row,
  Stat,
  Stats,
  TableWrap,
} from '../styles';
import { DiffLine, fieldsText } from '../helpers';
import { ChangesList } from './ChangesList';

/** Подробности редакции: счётчики, файлы архива, изменения, действия. */
export function EditionDetail({
  edition,
  canManage,
  activeTitle,
  titleOf,
}: {
  edition: FsnbEdition;
  canManage: boolean;
  activeTitle: string | null;
  /** Название редакции по метке («fsnb-2022-dop18» → «ФСНБ-2022, дополнение 18»). */
  titleOf: (label: string) => string;
}) {
  const [activate, { isLoading: activating }] = useActivateFsnbEditionMutation();
  const [remove, { isLoading: removing }] = useDeleteFsnbEditionMutation();
  const [retry, { isLoading: retrying }] = useRetryFsnbEditionMutation();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const s = edition.stats ?? {};
  const works = total(s.works);
  const resources = s.resources ?? {};
  const files = s.files ?? [];
  const diff = edition.diff;
  const canActivate =
    canManage && (edition.status === 'pending_review' || edition.status === 'archived');
  const canDelete =
    canManage && (edition.status === 'pending_review' || edition.status === 'failed');

  async function onActivate() {
    const was = activeTitle
      ? `Действующая сейчас «${activeTitle}» уйдёт в архив — к ней можно вернуться.`
      : '';
    if (
      !window.confirm(
        `Ввести в действие «${edition.title}»?\n\nС этого момента проверки расценок, подбор материалов и нормы для ППР пойдут по ней. ${was}`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      const r = await activate(edition.id).unwrap();
      setDone(
        `«${r.edition.title}» действует.${r.previous ? ' Прежняя редакция в архиве.' : ''}${
          r.carried_over.length > 0
            ? ` Из прежней перенесены сборники: ${r.carried_over.map(([src, n]) => `${sourceLabel(src)} (${fmtNum(n)})`).join(', ')}.`
            : ''
        }`,
      );
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось ввести редакцию в действие.'));
    }
  }

  async function onDelete() {
    if (!window.confirm(`Удалить «${edition.title}» вместе с загруженными данными?`)) return;
    setError(null);
    try {
      await remove(edition.id).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось удалить редакцию.'));
    }
  }

  async function onRetry() {
    setError(null);
    try {
      await retry(edition.id).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Не удалось повторить разбор.'));
    }
  }

  return (
    <Card>
      <Row>
        <BlockTitle>{edition.title}</BlockTitle>
        <Badge $tone={editionStatusTone(edition.status)}>
          {editionStatusLabel(edition.status)}
        </Badge>
        <Grow />
        {canActivate && (
          <Button
            type="button"
            $variant="primary"
            $size="sm"
            disabled={activating}
            onClick={onActivate}
          >
            {activating
              ? 'Вводим…'
              : edition.status === 'archived'
                ? 'Вернуть в действие'
                : 'Ввести в действие'}
          </Button>
        )}
        {edition.status === 'failed' && canManage && (
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            disabled={retrying}
            onClick={onRetry}
          >
            {retrying ? 'Запускаем…' : 'Повторить разбор'}
          </Button>
        )}
        {canDelete && (
          <Button type="button" $variant="ghost" $size="sm" disabled={removing} onClick={onDelete}>
            Удалить
          </Button>
        )}
      </Row>
      <Lead>
        {edition.decree ?? 'Приказ не указан'}
        {edition.file_name
          ? ` · архив «${edition.file_name}», ${sizeLabel(edition.file_size)}`
          : ''}
        {edition.uploaded_by_name ? ` · загрузил(а) ${edition.uploaded_by_name}` : ''}
        {` · ${fmtDateTime(edition.imported_at ?? edition.created_at)}`}
        {edition.activated_at
          ? ` · в действии с ${fmtDateTime(edition.activated_at)}${edition.activated_by_name ? ` (${edition.activated_by_name})` : ''}`
          : ''}
      </Lead>
      {!canManage && edition.status === 'pending_review' && (
        <Alert $tone="info">
          Ввести редакцию в действие может Сметчик+ или руководитель: от действующей редакции
          зависят деньги во всех проверках расценок.
        </Alert>
      )}
      {error && <Alert $tone="danger">{error}</Alert>}
      {done && <Alert $tone="success">{done}</Alert>}
      {edition.status === 'importing' && (
        <Alert $tone="info">
          Разбираем архив: нормы, состав ресурсов, цены и разделы сборников, затем сравниваем с
          действующей редакцией. Обычно это минута-две — страница обновится сама.
        </Alert>
      )}
      {edition.status === 'failed' && (
        <Alert $tone="danger">{edition.error ?? 'Разбор архива не удался.'}</Alert>
      )}

      {edition.status !== 'importing' && edition.status !== 'failed' && (
        <Stats>
          <Stat>
            <div className="label">Нормы</div>
            <div className="value">{fmtNum(works)}</div>
            <div className="sub">
              {Object.entries(s.works ?? {})
                .map(([bt, n]) => `${bt} — ${fmtNum(n)}`)
                .join(', ')}
            </div>
          </Stat>
          <Stat>
            <div className="label">Ресурсы ФСБЦ</div>
            <div className="value">{fmtNum(resources.fsbc ?? 0)}</div>
            <div className="sub">
              строк состава норм: {fmtNum(s.work_resources ?? 0)}
              {s.sections ? ` · разделов сборников: ${fmtNum(s.sections)}` : ''}
            </div>
          </Stat>
          <Stat>
            <div className="label">Сборники ФСНБ-2020 для ФЕР</div>
            <div className="value">
              {fmtNum((resources.fssc ?? 0) + (resources.fsem ?? 0) + (resources.fsscpg ?? 0))}
            </div>
            <div className="sub">
              ФССЦ {fmtNum(resources.fssc ?? 0)}, ФСЭМ {fmtNum(resources.fsem ?? 0)}, ФССЦпг{' '}
              {fmtNum(resources.fsscpg ?? 0)}
              {s.carried_from ? ` · перенесены из «${titleOf(s.carried_from)}»` : ''}
            </div>
          </Stat>
        </Stats>
      )}

      {diff?.base_edition && (
        <>
          <Divider />
          <BlockTitle>Что изменилось против «{titleOf(diff.base_edition)}»</BlockTitle>
          <Lead>
            Нормы: <DiffLine side={diff.works} />
            {fieldsText(diff.works?.fields) && (
              <>
                <br />
                <Muted>В изменённых нормах: {fieldsText(diff.works?.fields)}.</Muted>
              </>
            )}
            <br />
            Ресурсы ФСБЦ: <DiffLine side={diff.resources} />
          </Lead>
          <ChangesList edition={edition} />
        </>
      )}
      {edition.status === 'pending_review' && diff && !diff.base_edition && (
        <Note>Действующей редакции при загрузке не было — сравнивать не с чем.</Note>
      )}

      {files.length > 0 && (
        <>
          <Divider />
          <BlockTitle>Файлы архива</BlockTitle>
          <TableWrap>
            <Grid>
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Что это</th>
                  <th className="num">Размер</th>
                  <th>Загружено</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.name}>
                    <td>{f.name}</td>
                    <td>{f.label}</td>
                    <td className="num">{sizeLabel(f.size)}</td>
                    <td>
                      {f.loaded ? (
                        <>
                          {typeof f.works === 'number' && `норм ${fmtNum(f.works)}`}
                          {typeof f.abstract_resources === 'number' &&
                            f.abstract_resources > 0 &&
                            `, материалов по проекту ${fmtNum(f.abstract_resources)}`}
                          {typeof f.groups === 'number' && `групп ${fmtNum(f.groups)}, `}
                          {typeof f.resources === 'number' && `строк ${fmtNum(f.resources)}`}
                          {typeof f.coefficients === 'number' &&
                            `коэффициентов ${fmtNum(f.coefficients)}, привязок ${fmtNum(f.bindings ?? 0)}`}
                          {f.note && <Muted>{f.note}</Muted>}
                        </>
                      ) : (
                        <Muted>{f.note ?? 'не используется'}</Muted>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          </TableWrap>
        </>
      )}
    </Card>
  );
}
